import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@config/config";
import MoviePoster from "@components/MoviePoster";
import SessionDetails from "@components/SessionDetails";
import V2tSteps from "@components/V3/SuccessWithCinema/V2tSteps";
import V3SessionButton from "@components/V3/V3SessionButton";
import { getSessionType } from "@utils/ticketUtils";
import "./style.scss";

const Phase = {
    ACTIVE: "active",
    CONFIRMED_FINAL: "confirmed_final",
    ERROR: "error",
};

// Mirrors ticket_purchase.book_with_cinema_status, set by the bot via
// /api/book_my_tickets_cinema_fees.
const BookWithCinemaStatus = {
    AWAITING_FEES: 0,      // bot still to send booking/upgrade fees
    AWAITING_PURCHASE: 1,  // bot still to complete the purchase
    PURCHASE_COMPLETE: 2,  // bot has completed the purchase
    CANCELLABLE: 3,        // payment failed, bot to cancel at the cinema
};

const POLL_INTERVAL = 5000;
// The page tells the customer this takes 3-5 minutes, so allow headroom past that.
const MAX_POLL_TIME = 360000;
// A single flaky response shouldn't kill a poll this long.
const MAX_CONSECUTIVE_FAILURES = 3;

const SuccessWithCinema = () => {
    const [phase, setPhase] = useState(Phase.ACTIVE);
    const [cinemaBookingData, setCinemaBookingData] = useState(null);
    const [barcodeFailed, setBarcodeFailed] = useState(false);

    const pollTimeoutRef = useRef(null);
    const pollStartTimeRef = useRef(null);
    const failureCountRef = useRef(0);

    const { movie, session, seats, booking_reference, barcode_url } = cinemaBookingData || {};

    const experience_type = session ? getSessionType(session) : null;

    const candyBarVouchers = cinemaBookingData?.candy_bar_vouchers || [];

    // Get UUID from URL
    const [uuid] = useState(() => {
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get("id");
    });

    useEffect(() => {
        if (!uuid) return;

        pollStartTimeRef.current = Date.now();

        const scheduleNextPoll = () => {
            if (Date.now() - pollStartTimeRef.current < MAX_POLL_TIME) {
                pollTimeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL);
            } else {
                setPhase(Phase.ERROR);
            }
        };

        const pollStatus = () => {
            axios
                .get(`${API_BASE_URL}/fetchCinemaBooking?id=${uuid}`)
                .then((response) => {
                    if (!response.data.success || !response.data.data) {
                        setPhase(Phase.ERROR);
                        return;
                    }

                    failureCountRef.current = 0;

                    const { data } = response.data;
                    const status = data.book_with_cinema_status;

                    // Show what we know as soon as the bot picks the booking up,
                    // and keep it fresh while it works.
                    if (
                        status === BookWithCinemaStatus.AWAITING_PURCHASE ||
                        status === BookWithCinemaStatus.PURCHASE_COMPLETE
                    ) {
                        setCinemaBookingData(data);
                    }

                    // Only status 2 means the cinema booking is actually done.
                    if (status === BookWithCinemaStatus.PURCHASE_COMPLETE) {
                        setPhase(Phase.CONFIRMED_FINAL);
                        return;
                    }

                    if (status === BookWithCinemaStatus.CANCELLABLE) {
                        setPhase(Phase.ERROR);
                        return;
                    }

                    scheduleNextPoll();
                })
                .catch((err) => {
                    console.error("Error polling cinema booking status:", err);
                    failureCountRef.current += 1;

                    if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
                        setPhase(Phase.ERROR);
                        return;
                    }

                    scheduleNextPoll();
                });
        };

        pollStatus();

        return () => {
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        };
    }, [uuid]);


    if (phase === Phase.CONFIRMED_FINAL && cinemaBookingData) {
        return (
            <div className="success-with-cinema">
                <div className="success-with-cinema__container">
                    <div className="success-with-cinema__confirmation">
                        <h1 className="success-with-cinema__heading">Order Confirmed</h1>
                        <p className="success-with-cinema__text">
                            Our AI Agent has completed your cinema booking,
                            we&#39;ve emailed your booking confirmation
                        </p>
                    </div>

                    <div className="success-with-cinema__info">
                        <MoviePoster movie={movie} size="small" movieLink={false} />
                        <SessionDetails session={session} />
                    </div>

                    <div className="success-with-cinema__details">
                        <div className="success-with-cinema__reference-label">Seats</div>
                        <div className="success-with-cinema__reference-text">{seats}</div>
                        <div className="success-with-cinema__reference-label">
                            Cinema booking number
                        </div>
                        <div className="success-with-cinema__reference-text">
                            {booking_reference || "Awaiting confirmation from the cinema"}
                        </div>
                        {barcode_url && !barcodeFailed && (
                            <div className="success-with-cinema__barcode">
                                <img
                                    src={barcode_url}
                                    alt={`Barcode for booking ${booking_reference}`}
                                    className="success-with-cinema__barcode-image"
                                    onError={() => setBarcodeFailed(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (phase === Phase.ERROR) {
        return (
            <div className="success-with-cinema">
                <div className="success-with-cinema__container">
                    <div className="success-with-cinema__loading">
                        <h2 className="success-with-cinema__heading">Something went wrong</h2>
                        <p className="success-with-cinema__text">
                            We couldn&#39;t confirm your cinema booking. Please try refreshing
                            the page or contact support.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── ACTIVE — all three sections visible simultaneously ──
    return (
        <div className="success-with-cinema">
            <V2tSteps phase={phase} phaseList={Phase} />

            {/* Loader — always visible during active phase */}
            <div className="success-with-cinema__container">
                <div className="success-with-cinema__loading">
                    <h2 className="success-with-cinema__heading">Securing Your Seats</h2>

                    <p className="success-with-cinema__text">
                        Our booking assistant is finalising your seats at the cinema.
                        This usually takes 3-5 minutes...
                    </p>

                    <div className="success-with-cinema__loader"></div>
                </div>
            </div>

            {/* Confirmed pending — appears as soon as booking data arrives */}
            {cinemaBookingData && (
                <div className="success-with-cinema__container">
                    <div className="success-with-cinema__confirmation">
                        <p className="paragraph">Hi {cinemaBookingData.session.first_name},</p>

                        <p className="paragraph">
                            Great news! Our booking assistant has received your request to secure
                            your seats using your movie vouchers:
                        </p>

                        <div className="success-with-cinema__info">
                            <MoviePoster movie={movie} size="small" movieLink={false} />
                            <SessionDetails session={session} />
                        </div>

                        <div className="success-with-cinema__sessions">
                            {session?.items && session.items.length > 0 ? (
                                session.items.map((item, index) => (
                                    <V3SessionButton
                                        key={index}
                                        session={{
                                            ...session,
                                            experience_type,
                                            ticket_price_cents: item.price_cents,
                                            rrp_pricing_cents: item.rrp_pricing_cents,
                                            saving: item.saving,
                                        }}
                                    />
                                ))
                            ) : (
                                <V3SessionButton
                                    session={{ ...session, experience_type }}
                                />
                            )}
                        </div>

                        <p className="paragraph">
                            Number of tickets: {cinemaBookingData.session.quantity}
                        </p>

                        {candyBarVouchers.length > 0 && (
                            <div className="success-with-cinema__btns">
                                <p className="paragraph">Your candy bar vouchers are below:</p>
                                {candyBarVouchers.map((v, i) => (
                                    <a
                                        key={i}
                                        href={v.voucher_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="button button__primary voucher-list__button"
                                    >
                                        Candy Bar Voucher {i + 1}
                                    </a>
                                ))}
                            </div>
                        )}

                        <p className="paragraph">
                            We&#39;re now securing your booking and will send a follow-up email
                            shortly with your cinema confirmation. This usually takes around 3-5
                            minutes.
                        </p>

                        <p className="paragraph">
                            Please present your booking confirmation at the cinema for entry.
                        </p>

                        <p className="paragraph">Enjoy the movie!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuccessWithCinema;
